import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router';
import { api, useAPIContext } from '../../../api/context';
import { Title } from '../../../utils/title';
import dialogs from '../../../dialogs';
import Layout from '../campaigns/Layout';
import ProductCard from './ProductCard';
import './ProductTinder.scss';

const STACK_SIZE = 2; // Number of cards to show in stack
const PREFETCH_THRESHOLD = 0.3; // Fetch more when 30% of deck remains

/**
 * ProductTinder - Tinder-style product review experience
 * 
 * Manages a deck of cards with proper state handling:
 * - Dismiss: animate out, then remove from deck
 * - Review: open dialog (no animation), on complete animate out and remove
 * - Smart prefetching: loads more items when deck runs low
 */
const ProductTinder = () => {
  const { token } = useAPIContext();
  const [deck, setDeck] = useState([]); // Visible cards in the stack
  const [isLoading, setIsLoading] = useState(true);
  const [reviewingItem, setReviewingItem] = useState(null);
  const [animatingOutId, setAnimatingOutId] = useState(null);
  
  // Track fetching state and seen IDs to avoid duplicates
  const isFetchingMore = useRef(false);
  const seenProductIds = useRef(new Set());
  const initialDeckSize = useRef(0);

  // Initial fetch
  const fetchItems = useCallback(async () => {
    setIsLoading(true);
    seenProductIds.current.clear();

    try {
      const response = await api(token).v1.products.feed.query();

      if (response.status === 200 && response.body) {
        const items = response.body.items || [];
        // Track seen IDs
        items.forEach(item => seenProductIds.current.add(item.product.id));
        initialDeckSize.current = items.length;
        setDeck(items);
      }
    } catch (err) {
      setDeck([]);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Background fetch for more items (appends to deck)
  const fetchMoreItems = useCallback(async () => {
    if (isFetchingMore.current) return;
    isFetchingMore.current = true;

    try {
      const response = await api(token).v1.products.feed.query();

      if (response.status === 200 && response.body) {
        const newItems = (response.body.items || []).filter(
          item => !seenProductIds.current.has(item.product.id)
        );
        
        if (newItems.length > 0) {
          // Track new IDs
          newItems.forEach(item => seenProductIds.current.add(item.product.id));
          setDeck(current => [...current, ...newItems]);
        }
      }
    } catch (err) {
      // Silent fail for background fetch
    } finally {
      isFetchingMore.current = false;
    }
  }, [token]);

  // Smart prefetch: trigger when deck is running low
  useEffect(() => {
    if (isLoading || deck.length === 0) return;
    
    const threshold = Math.max(3, Math.floor(initialDeckSize.current * PREFETCH_THRESHOLD));
    
    if (deck.length <= threshold && !isFetchingMore.current) {
      fetchMoreItems();
    }
  }, [deck.length, isLoading, fetchMoreItems]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Remove the front card from deck
  const removeTopCard = useCallback(() => {
    setDeck(current => current.slice(1));
    setAnimatingOutId(null);
  }, []);

  // Called when dismiss animation completes
  const handleDismissComplete = useCallback(() => {
    const item = deck[0];
    if (item) {
      // Fire API call (don't await, fire and forget)
      api(token).v1.products.dismiss.post({ product_id: item.product.id });
    }
    removeTopCard();
  }, [deck, token, removeTopCard]);

  // Called when user clicks review button
  const handleReviewClick = useCallback(() => {
    const item = deck[0];
    if (!item) return;
    setReviewingItem(item);
  }, [deck]);

  // Called when review dialog is closed without completing
  const handleReviewClose = useCallback(() => {
    setReviewingItem(null);
  }, []);

  // Called when review is submitted successfully
  const handleReviewComplete = useCallback(() => {
    setReviewingItem(null);
    // Trigger exit animation, then remove
    setAnimatingOutId(deck[0]?.product.id);
  }, [deck]);

  // Called when review exit animation completes
  const handleReviewAnimateOutComplete = useCallback(() => {
    removeTopCard();
  }, [removeTopCard]);

  const Dialog = reviewingItem ? dialogs.products.getDialog(reviewingItem) : null;
  const visibleDeck = deck.slice(0, STACK_SIZE);
  const topItem = deck[0];

  if (isLoading) {
    return (
      <>
        <Title value="Takes" />
        <Layout>
          <div className="tinder">
            <Link to="/campaigns" className="tinder__back">
              ← Back to Campaigns
            </Link>
            <div className="tinder__loading">
              <div className="tinder__spinner" />
            </div>
          </div>
        </Layout>
      </>
    );
  }

  if (!topItem) {
    return (
      <>
        <Title value="Takes" />
        <Layout>
          <div className="tinder">
            <Link to="/campaigns" className="tinder__back">
              ← Back to Campaigns
            </Link>
            <div className="tinder__empty">
              <div className="tinder__empty-icon">✓</div>
              <p>All caught up!</p>
              <span>You've reviewed all available products</span>
              <button onClick={fetchItems} className="tinder__refresh">
                Check for more
              </button>
            </div>
          </div>
        </Layout>
      </>
    );
  }

  return (
    <>
      <Title value="Takes" />
      <Layout>
        <div className="tinder">
          <Link to="/campaigns" className="tinder__back">
            ← Back to Campaigns
          </Link>
          <div className="tinder__header">
            <h2><strong>Swipe, review, repeat!</strong> Tell us what you think about the products you've tried!</h2>
          </div>

          <div className="tinder__stack">
            {/* Render in reverse so first item is on top (higher z-index via CSS) */}
            {visibleDeck.map((item, index) => {
              const itemId = `${item.type}-${item.product.id}`;
              const isTop = index === 0;
              const shouldAnimateOut = animatingOutId === item.product.id;

              return (
                <ProductCard
                  key={itemId}
                  item={item}
                  stackPosition={index}
                  onDismiss={isTop ? handleDismissComplete : undefined}
                  onReview={isTop ? handleReviewClick : undefined}
                  animateOut={shouldAnimateOut ? 'right' : null}
                  onAnimateOutComplete={handleReviewAnimateOutComplete}
                />
              );
            })}
          </div>

          {reviewingItem && Dialog && (
            <Dialog
              item={reviewingItem}
              onClose={handleReviewClose}
              onSubmit={handleReviewComplete}
            />
          )}
        </div>
      </Layout>
    </>
  );
};

export default ProductTinder;
